import { RadioButton, RadioButtonGroup } from "@octopusdeploy/design-system-components";
import { GitCommitMethods } from "@octopusdeploy/legacy-action-properties";
import type { ArgoCDImageUpdateProperties } from "@octopusdeploy/legacy-action-properties";
import type { InlineStatusQuery } from "@octopusdeploy/octopus-react-client";
import type { PackageReference, ProcessType } from "@octopusdeploy/octopus-server-client";
import { PackageAcquisitionLocation, GetNamedPackageReferences, ActionExecutionLocation } from "@octopusdeploy/octopus-server-client";
import type { PropsWithChildren } from "react";
import * as React from "react";
import { useOptionalProcessContext } from "~/areas/projects/components/Process/Contexts/ProcessContext";
import { useFeedsFromContext, useRefreshFeedsFromContext } from "~/areas/projects/components/Process/Contexts/ProcessFeedsContextProvider";
import { TargetRoles } from "~/areas/projects/components/Process/types";
import { ActionInputs } from "~/components/ActionInputs/ActionInputs";
import type { ActionSummaryProps } from "~/components/Actions/actionSummaryProps";
import type { ArgoCdInstancesAndApplications } from "~/components/Actions/argocd/ArgoCDActionCommon";
import { useArgoCDContext } from "~/components/Actions/argocd/ArgoCDActionCommon";
import { DeploymentPreviewExpander } from "~/components/Actions/argocd/DeploymentPreview";
import type { ActionWithFeeds } from "~/components/Actions/commonActionHelpers";
import pluginRegistry from "~/components/Actions/pluginRegistry";
import type { ActionEditProps } from "~/components/Actions/pluginRegistry";
import type { ScriptPackageProperties } from "~/components/Actions/script/ScriptPackageReferenceDialog";
import { BaseComponent } from "~/components/BaseComponent/BaseComponent";
import FeatureToggleVisibility from "~/components/FeatureToggle/New/FeatureToggleVisibility";
import ExternalLink from "~/components/Navigation/ExternalLink/index";
import type { SummaryNode } from "~/components/form/Sections/ExpandableFormSection";
import ExpandableFormSection from "~/components/form/Sections/ExpandableFormSection";
import ExpanderSectionHeading from "~/components/form/Sections/FormSectionHeading";
import Summary from "~/components/form/Sections/Summary";
import { VariableLookupText } from "~/components/form/VariableLookupText";
import { Note } from "~/components/form/index";
class ArgoCDImageUpdateActionSummary extends BaseComponent<ActionSummaryProps, never> {
    constructor(props: ActionSummaryProps) {
        super(props);
    }
    render() {
        return <div>Update ArgoCD applications with new container image versions.</div>;
    }
    static displayName = "ArgoCDImageUpdateActionSummary";
}
type ArgoCDImageUpdateActionProps = ActionEditProps<ArgoCDImageUpdateProperties, ScriptPackageProperties>;
type ArgoCDInfrastructureMetadata = {
    argoContext: InlineStatusQuery<ArgoCdInstancesAndApplications, null>;
    processType?: ProcessType;
};
type ArgoCDImageUpdateActionInternalProps = ArgoCDImageUpdateActionProps & ActionWithFeeds & ArgoCDInfrastructureMetadata;
export class ArgoCDImageUpdateActionEdit extends BaseComponent<ArgoCDImageUpdateActionInternalProps, {}> {
    constructor(props: ArgoCDImageUpdateActionInternalProps) {
        super(props);
    }
    packageReferenceSummary = () => {
        const namedPackageReferences = GetNamedPackageReferences(this.props.packages);
        if (namedPackageReferences.length === 0) {
            return Summary.placeholder("No additional packages referenced");
        }
        return Summary.summary(`${namedPackageReferences.length} package references`);
    };
    setPackagesWithoutAcquisition = (packages: Array<PackageReference<ScriptPackageProperties>>, initialise?: boolean) => {
        //remove acquisition data from each package
        const unacquiredPackages = packages.map((item) => ({ ...item, AcquisitionLocation: PackageAcquisitionLocation.NotAcquired }));
        this.props.setPackages(unacquiredPackages, initialise);
    };
    commitMethodSummary(): SummaryNode {
        if (this.getCommitMethod() === GitCommitMethods.DirectCommit) {
            return Summary.placeholder("Direct commit");
        }
        else {
            return Summary.summary(`Commit with pull request`);
        }
    }
    getCommitMethod(): string {
        return this.props.properties["Octopus.Action.ArgoCD.CommitMethod"] ?? GitCommitMethods.DirectCommit;
    }
    render() {
        // replace the default setPackages, to enforce no packages acquisition for this step.
        const { setPackages, ...rest } = this.props;
        return (<div>
                <ExpanderSectionHeading title="Input" description="When this step runs, Octopus will update the specified image tag in your configuration files."/>
                <ExpandableFormSection title={"Container Images"} isExpandedByDefault={this.props.expandedByDefault} errorKey="Octopus.Action.Script.Packages" summary={this.packageReferenceSummary()} help={"Add the container image(s) that Octopus will update during deployment."}>
                    <Note>
                        Learn more about <ExternalLink href="ScriptStepPackageReferences">package/container references</ExternalLink>.
                    </Note>

                    <ActionInputs actionUIDefinition={[{ type: "container-image-reference-list", key: "container-image-reference-list", hideAcquisitionChoices: true, hasRelatedTriggersDependencies: true }]} dependencies={this.props.actionInputsDependencies}/>
                </ExpandableFormSection>
                <>
                    <ExpanderSectionHeading title="Output" description="A new version of your configuration files with the updated image tag will be commited to Git, which will trigger a sync from Argo CD."/>
                    <DeploymentPreviewExpander argoContext={this.props.argoContext}/>
                    <ExpandableFormSection errorKey="Octopus.Action.ArgoCD.CommitMessage" title="Commit Message" summary={!!this.props.properties["Octopus.Action.ArgoCD.CommitMessageSummary"] ? Summary.summary(this.props.properties["Octopus.Action.ArgoCD.CommitMessageSummary"]) : Summary.summary("No summary provided")} help={"Provide a message for the commit to Git."} isExpandedByDefault={this.props.expandedByDefault}>
                        <VariableLookupText key="Octopus.Action.UpdateImageVersion.Destination.CommitMessageSummary" localNames={this.props.localNames} value={this.props.properties["Octopus.Action.ArgoCD.CommitMessageSummary"] ?? ""} onChange={(summary) => this.props.setProperties({ ["Octopus.Action.ArgoCD.CommitMessageSummary"]: summary })} label={"Summary"} error={this.props.getFieldError("Octopus.Action.UpdateImageVersion.Destination.CommitMessageSummary")} multiline={false}/>
                        <VariableLookupText key="Octopus.Action.UpdateImageVersion.Destination.CommitMessageDescription" localNames={this.props.localNames} value={this.props.properties["Octopus.Action.ArgoCD.CommitMessageDescription"] ?? ""} onChange={(description) => this.props.setProperties({ ["Octopus.Action.ArgoCD.CommitMessageDescription"]: description })} label={"Optional description"} error={this.props.getFieldError("Octopus.Action.UpdateImageVersion.Destination.CommitMessageDescription")} multiline={true} helperText={"Octopus will automatically add image version details to the description."}/>
                    </ExpandableFormSection>
                    <FeatureToggleVisibility toggle={"argocd-create-pull-request"}>
                        <ExpandableFormSection errorKey="Octopus.Action.ArgoCD.CommitMethod" title="Git Commit Method" summary={this.commitMethodSummary()} help={"Select how you want to commit your configuration to Git."} isExpandedByDefault={this.props.expandedByDefault}>
                            <RadioButtonGroup value={this.getCommitMethod()} onChange={(val) => this.props.setProperties({ ["Octopus.Action.ArgoCD.CommitMethod"]: val })} error={this.props.getFieldError("Octopus.Action.ArgoCD.CommitMethod")}>
                                <RadioButton value={GitCommitMethods.DirectCommit} label="Direct commit"/>
                                <RadioButton value={GitCommitMethods.PullRequest} label="Commit with pull request"/>
                            </RadioButtonGroup>
                        </ExpandableFormSection>
                    </FeatureToggleVisibility>
                </>
            </div>);
    }
    static displayName = "ArgoCDImageUpdateActionEdit";
}
export function ArgoCDImageUpdateAction(props: PropsWithChildren<ArgoCDImageUpdateActionProps>) {
    const feeds = useFeedsFromContext();
    const refreshFeeds = useRefreshFeedsFromContext();
    const processContext = useOptionalProcessContext();
    return <ArgoCDImageUpdateActionEdit {...props} feeds={feeds} processType={processContext?.selectors.getProcessType()} refreshFeeds={refreshFeeds} argoContext={useArgoCDContext()}/>;
}
const defaultCommitMessageSummary = "Octopus Deploy updated image versions";
pluginRegistry.registerAction({
    executionLocation: ActionExecutionLocation.AlwaysOnServer,
    canRunOnWorker: false,
    canRunInContainer: false,
    actionType: "Octopus.ArgoCD.ImageUpdate",
    summary: (properties, targetRolesAsCSV) => <ArgoCDImageUpdateActionSummary properties={properties} targetRolesAsCSV={targetRolesAsCSV}/>,
    editSections: {
        //top: (props: ActionEditProps<UpdateImageVersionProperties>) => ,
        default: (props: ArgoCDImageUpdateActionProps) => <ArgoCDImageUpdateAction {...props}/>,
    },
    getInitialProperties: () => ({ "Octopus.Action.ArgoCD.CommitMessageSummary": defaultCommitMessageSummary }),
    canHaveChildren: (step) => false,
    canBeChild: true,
    targetRoleOption: (action) => TargetRoles.None,
    hasPackages: (action) => true,
    docsLink: "updateArgoCDAppImages",
});
